import { useEffect, useState, useCallback } from "react";
import moment from "moment";
import { AxiosInstance } from "../services/Api";
import { requestFcmToken, onForegroundMessage } from "../firebase";

/**
 * Notifications for the header bell.
 *
 * FCM does NOT replace /school/Eventlist — the two do different jobs:
 *   - /school/Eventlist  -> stored history, survives refresh, populates the bell list
 *   - FCM onMessage      -> real-time delivery of new pushes (lost if the tab was closed)
 * So we load the history once on mount AND prepend live FCM messages on top.
 *
 * @param {object}  params
 * @param {string|number} params.clientId  client_data[0].client_id (for Eventlist)
 * @param {string|number} params.schoolId  client_data[0].school_id (for save-fcm-token)
 * @param {string|number} params.userId    user.id
 * @param {string|number} params.role       user.role.id
 */
export function useNotifications({ clientId, schoolId, userId, role } = {}) {
  const [notifications, setNotifications] = useState([]);

  // 1. Load stored history once (replaces polling, not the endpoint).
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;

    AxiosInstance.post("/school/Eventlist", { client_id: clientId })
      .then((response) => {
        if (cancelled) return;
        if (response.data?.status && Array.isArray(response.data.data)) {
          const formatted = response.data.data.map((event) => ({
            id: event.id,
            subject: event.event_subject || "",
            description: event.event_description || "",
            venue: event.event_venue || "",
            startDate: moment(event.start_date).format("DD MMM YYYY"),
            time: moment(event.start_date).fromNow(),
          }));
          setNotifications(formatted);
        }
      })
      .catch((err) => console.error("Error fetching notifications:", err));

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // 2. Register the FCM registration token with the backend (device: 1 = web).
  useEffect(() => {
    if (!userId || !schoolId) return;
    let cancelled = false;

    (async () => {
      try {
        const fcm_token = await requestFcmToken();
        if (!fcm_token || cancelled) return;
        await AxiosInstance.post("/login/save-fcm-token", {
          school_id: schoolId,
          user_id: userId,
          role,
          fcm_token,
          device: 1,
        });
      } catch (e) {
        console.error("FCM registration failed:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, schoolId, role]);

  // 3. Prepend live foreground pushes onto the list (mapped to the bell shape).
  useEffect(() => {
    const unsubscribe = onForegroundMessage((payload) => {
      setNotifications((prev) => [
        {
          id: payload.messageId || `fcm-${Date.now()}`,
          subject: payload.notification?.title || payload.data?.title || "",
          description: payload.notification?.body || payload.data?.body || "",
          venue: payload.notification?.body || payload.data?.body || "",
          startDate: moment().format("DD MMM YYYY"),
          time: "Just now",
          isLive: true,
        },
        ...prev,
      ]);
    });
    return unsubscribe;
  }, []);

  const clearNotifications = useCallback(() => setNotifications([]), []);

  return {
    notifications,
    notificationCount: notifications.length,
    clearNotifications,
  };
}

export default useNotifications;
